import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useBranch } from "@/contexts/BranchContext";
import { DashboardWidgetItem, DashboardAnalyticsData, PeriodType, WidgetWidth } from "@/types/dashboard";
import { WIDGET_REGISTRY } from "@/lib/dashboard/widgetRegistry";
import { DashboardToolbar } from "./DashboardToolbar";
import { WidgetWrapper } from "./WidgetWrapper";
import { WidgetLibraryModal } from "./WidgetLibraryModal";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, X, Landmark, UserPlus, Receipt, Package, ArrowRight,
  ShieldCheck, AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Import all modular widgets
import {
  RevenueWidget,
  NetRevenueWidget,
  GrossProfitWidget,
  NetProfitWidget,
  ProfitMarginWidget,
  TotalExpensesWidget,
  CogsWidget,
  BankBalanceWidget,
  CashInHandWidget,
  ReceivablesWidget,
  PayablesWidget,
  OverdueInvoicesWidget,
  UpcomingPaymentsWidget,
  GstLiabilityWidget,
  GstItcWidget,
  QuickActionsWidget,
  QuickActionKey,
  SalesExpensesChartWidget,
  ExpenseBreakdownWidget,
  CashFlowChartWidget,
  ActivityFeedWidget,
  HealthScoreWidget,
  AiAdvisorWidget,
  LowStockWidget,
  CustomerInsightsWidget,
  SupplierInsightsWidget,
  GstDeadlinesWidget,
  MonthlyComparisonWidget,
  LiveClockWidget,
} from "./WidgetComponents";
import { GSTInvoiceForm } from "@/components/accounting/GSTInvoiceForm";
import { ReceiptVoucherForm } from "@/components/accounting/ReceiptVoucherForm";
import { RecordExpenseDialog } from "@/components/accounting/RecordExpenseDialog";
import { QuickPartyModal } from "@/components/accounting/QuickPartyModal";

export const CommandCenterDashboard = () => {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { branches, selectedBranch, selectBranch } = useBranch();
  const navigate = useNavigate();

  // Period and date states
  const [period, setPeriod] = useState<PeriodType>("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [branchId, setBranchId] = useState<string>("all");

  // Layout states
  const [activeLayout, setActiveLayout] = useState<DashboardWidgetItem[]>([]);
  const [savedLayout, setSavedLayout] = useState<DashboardWidgetItem[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetLibraryOpen, setWidgetLibraryOpen] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [savingLayout, setSavingLayout] = useState(false);

  // In-Dashboard Quick Accounting Action Modal State
  const [activeQuickAction, setActiveQuickAction] = useState<QuickActionKey | null>(null);

  // Analytics data state
  const [analytics, setAnalytics] = useState<DashboardAnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string | null>(null);

  // Getting started checklist for new businesses
  const [checklistDismissed, setChecklistDismissed] = useState(false);

  // Helper to extract auth header
  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const sess = localStorage.getItem("accoupro_auth_session");
      if (sess) {
        const parsed = JSON.parse(sess);
        if (parsed.access_token) headers["Authorization"] = `Bearer ${parsed.access_token}`;
      }
      if (user?.id) headers["x-user-id"] = user.id;
      if (activeCompany?.id) headers["x-company-id"] = activeCompany.id;
    } catch {}
    return headers;
  }, [user?.id, activeCompany?.id]);

  // 1. Load User's Layout from Backend
  const loadLayout = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLayoutLoading(true);

    try {
      const res = await fetch(
        `/api/dashboard/layout?companyId=${activeCompany.id}&branchId=${branchId}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.layout)) {
        setActiveLayout(data.layout);
        setSavedLayout(data.layout);
      }
    } catch (err) {
      console.warn("[CommandCenter] Could not load saved layout from server, using fallback", err);
    } finally {
      setLayoutLoading(false);
    }
  }, [activeCompany?.id, branchId, getAuthHeaders]);

  // 2. Load Real-Time Financial Analytics from Backend
  const loadAnalytics = useCallback(async () => {
    if (!activeCompany?.id) return;
    setAnalyticsLoading(true);

    try {
      const qParams = new URLSearchParams({
        companyId: activeCompany.id,
        period,
        branchId,
      });
      if (period === "custom" && customFrom && customTo) {
        qParams.append("fromDate", customFrom);
        qParams.append("toDate", customTo);
      }

      const res = await fetch(`/api/dashboard/analytics?${qParams.toString()}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setAnalytics(json.data);
        setLastUpdatedTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
      }
    } catch (err) {
      console.error("[CommandCenter] Failed to load analytics", err);
      toast.error("Could not refresh dashboard metrics. Using cached data.");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeCompany?.id, period, customFrom, customTo, branchId, getAuthHeaders]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Handle in-dashboard quick action success
  const handleQuickActionSuccess = useCallback((msg: string) => {
    setActiveQuickAction(null);
    toast.success(msg);
    // Refresh analytics in background without page reload
    loadAnalytics();
  }, [loadAnalytics]);

  // Check for unsaved layout changes
  const hasUnsavedChanges = useMemo(() => {
    if (activeLayout.length !== savedLayout.length) return true;
    return JSON.stringify(activeLayout) !== JSON.stringify(savedLayout);
  }, [activeLayout, savedLayout]);

  // Save Layout to Backend Database
  const handleSaveLayout = async () => {
    if (!activeCompany?.id) return;
    setSavingLayout(true);

    try {
      const res = await fetch("/api/dashboard/layout", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          companyId: activeCompany.id,
          branchId,
          layout: activeLayout,
          version: 1,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSavedLayout(activeLayout);
        setIsEditMode(false);
        toast.success("Dashboard layout saved successfully!");
      } else {
        throw new Error(data.error || "Save failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save dashboard layout");
    } finally {
      setSavingLayout(false);
    }
  };

  // Cancel edit mode and revert changes
  const handleCancelEdit = () => {
    setActiveLayout(savedLayout);
    setIsEditMode(false);
  };

  // Reset to default layout
  const handleResetLayout = async () => {
    if (!activeCompany?.id) return;
    try {
      const res = await fetch("/api/dashboard/layout/reset", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ companyId: activeCompany.id, branchId }),
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.layout)) {
        setActiveLayout(data.layout);
        setSavedLayout(data.layout);
        setIsEditMode(false);
        toast.success("Dashboard layout restored to default!");
      }
    } catch (err) {
      toast.error("Failed to reset dashboard layout");
    }
  };

  // Add widget from library
  const handleAddWidget = (widgetId: string) => {
    const reg = WIDGET_REGISTRY[widgetId];
    if (!reg) return;

    setActiveLayout((prev) => {
      // If already present but hidden, make visible
      const existingIdx = prev.findIndex((w) => w.widgetId === widgetId);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], visible: true };
        return next;
      }
      // Otherwise append new widget
      return [
        ...prev,
        {
          id: `w-${widgetId}-${Date.now().toString(36)}`,
          widgetId,
          width: reg.defaultWidth,
          visible: true,
          order: prev.length,
          settings: {},
        },
      ];
    });

    toast.success(`"${reg.title}" added to dashboard`);
  };

  // Remove/hide widget
  const handleRemoveWidget = (widgetId: string) => {
    setActiveLayout((prev) => prev.filter((w) => w.widgetId !== widgetId));
  };

  // Cycle width (compact -> medium -> wide -> full -> compact)
  const handleToggleWidth = (index: number) => {
    setActiveLayout((prev) => {
      const next = [...prev];
      const curWidth = next[index].width;
      const widths: WidgetWidth[] = ["compact", "medium", "wide", "full"];
      const nextIdx = (widths.indexOf(curWidth) + 1) % widths.length;
      next[index] = { ...next[index], width: widths[nextIdx] };
      return next;
    });
  };

  // Reorder: Move Up / Earlier
  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    setActiveLayout((prev) => {
      const next = [...prev];
      const temp = next[index];
      next[index] = next[index - 1];
      next[index - 1] = temp;
      return next.map((it, idx) => ({ ...it, order: idx }));
    });
  };

  // Reorder: Move Down / Later
  const handleMoveDown = (index: number) => {
    setActiveLayout((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      const temp = next[index];
      next[index] = next[index + 1];
      next[index + 1] = temp;
      return next.map((it, idx) => ({ ...it, order: idx }));
    });
  };

  // Drag and Drop reordering handlers
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    setActiveLayout((prev) => {
      const next = [...prev];
      const [draggedItem] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, draggedItem);
      return next.map((item, idx) => ({ ...item, order: idx }));
    });

    setDraggedIndex(null);
  };

  // Render individual widget component by ID
  const renderWidgetContent = (widgetId: string, itemWidth?: WidgetWidth) => {
    const props = { analytics, loading: analyticsLoading, onRefresh: loadAnalytics };

    switch (widgetId) {
      case "live_clock":
        return <LiveClockWidget width={itemWidth} />;
      case "revenue":
        return <RevenueWidget {...props} />;
      case "net_revenue":
        return <NetRevenueWidget {...props} />;
      case "gross_profit":
        return <GrossProfitWidget {...props} />;
      case "net_profit":
        return <NetProfitWidget {...props} />;
      case "profit_margin":
        return <ProfitMarginWidget {...props} />;
      case "total_expenses":
        return <TotalExpensesWidget {...props} />;
      case "cogs":
        return <CogsWidget {...props} />;
      case "bank_balance":
        return <BankBalanceWidget {...props} />;
      case "cash_in_hand":
        return <CashInHandWidget {...props} />;
      case "receivables":
        return <ReceivablesWidget {...props} />;
      case "payables":
        return <PayablesWidget {...props} />;
      case "overdue_invoices":
        return <OverdueInvoicesWidget {...props} />;
      case "upcoming_payments":
        return <UpcomingPaymentsWidget {...props} />;
      case "gst_liability":
        return <GstLiabilityWidget {...props} />;
      case "gst_itc":
        return <GstItcWidget {...props} />;
      case "quick_actions":
        return (
          <QuickActionsWidget
            onSelectAction={(actKey) => {
              if (actKey === "stock_adjustment") {
                navigate("/dashboard/journals?type=stock-journal&action=new");
              } else {
                setActiveQuickAction(actKey);
              }
            }}
          />
        );
      case "sales_expenses_chart":
        return <SalesExpensesChartWidget {...props} />;
      case "expense_breakdown":
        return <ExpenseBreakdownWidget {...props} />;
      case "cash_flow_chart":
        return <CashFlowChartWidget {...props} />;
      case "activity_feed":
        return <ActivityFeedWidget {...props} />;
      case "health_score":
        return <HealthScoreWidget {...props} />;
      case "ai_advisor":
        return <AiAdvisorWidget {...props} />;
      case "low_stock_alerts":
        return <LowStockWidget {...props} />;
      case "customer_insights":
        return <CustomerInsightsWidget {...props} />;
      case "supplier_insights":
        return <SupplierInsightsWidget {...props} />;
      case "gst_deadlines":
        return <GstDeadlinesWidget {...props} />;
      case "monthly_comparison":
        return <MonthlyComparisonWidget {...props} />;
      default:
        // Graceful fallback for any extra widget ID in catalog
        const reg = WIDGET_REGISTRY[widgetId];
        return (
          <div className="p-4 rounded-xl bg-card border border-border flex items-center justify-between text-xs">
            <span className="font-semibold text-foreground">{reg?.title || widgetId}</span>
            <Badge variant="outline">Verified</Badge>
          </div>
        );
    }
  };

  const isBrandNewCompany = (analytics?.metrics.totalVouchersCount || 0) === 0;

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full overflow-hidden">
      {/* Dynamic Company Command Center Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-1 border-b border-slate-200/80">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold font-display text-slate-900 tracking-tight">
              {activeCompany?.tradeName || activeCompany?.name || "Business Command Center"}
            </h1>
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 font-semibold shadow-2xs">
              Command Center
            </Badge>
            {activeCompany?.industryLabel && (
              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-200">
                {activeCompany.industryLabel}
              </Badge>
            )}
            {activeCompany?.gstRegistered && activeCompany?.gstin && (
              <Badge variant="outline" className="text-[11px] font-mono bg-slate-100 text-slate-700 border-slate-200 hidden sm:inline-flex">
                GSTIN: {activeCompany.gstin}
              </Badge>
            )}
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            {activeCompany?.legalName && activeCompany.legalName !== activeCompany.tradeName ? `${activeCompany.legalName} • ` : ""}
            Real-time accounting intelligence & operational overview
          </p>
        </div>
      </div>

      {/* Customizable Dashboard Toolbar with Date Controls & Prominent Live Clock */}
      <DashboardToolbar
        period={period}
        onPeriodChange={setPeriod}
        customFrom={customFrom}
        customTo={customTo}
        onCustomDatesChange={(f, t) => {
          setCustomFrom(f);
          setCustomTo(t);
        }}
        selectedBranch={branchId}
        onBranchChange={setBranchId}
        branches={branches}
        isEditMode={isEditMode}
        onToggleEditMode={() => setIsEditMode((v) => !v)}
        onSaveLayout={handleSaveLayout}
        onCancelEdit={handleCancelEdit}
        onResetLayout={handleResetLayout}
        onOpenWidgetLibrary={() => setWidgetLibraryOpen(true)}
        onRefresh={loadAnalytics}
        loading={analyticsLoading}
        saving={savingLayout}
        lastUpdated={lastUpdatedTime}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Getting Started Guide for New Companies */}
      {isBrandNewCompany && !checklistDismissed && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900">
                  Welcome to AccouPro, {activeCompany?.tradeName || activeCompany?.name || "Partner"}!
                </h3>
                <p className="text-xs text-slate-500">
                  Your <strong>{activeCompany?.industryLabel || "Business"}</strong> command center is live. Let's record your first entries:
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => setChecklistDismissed(true)}
              title="Dismiss checklist"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3.5 rounded-xl border border-border bg-card/80 space-y-2 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="w-7 h-7 rounded-md bg-blue-500/10 text-blue-600 flex items-center justify-center">
                    <Landmark className="w-4 h-4" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">Step 1</Badge>
                </div>
                <h4 className="text-xs font-semibold text-foreground">Add Bank / UPI Ledger</h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                  Set up your company bank account or UPI handle for receipts and payments.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => navigate("/dashboard/ledgers")} className="w-full text-xs h-7 gap-1 mt-2">
                <span>Add Bank</span>
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-card/80 space-y-2 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="w-7 h-7 rounded-md bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                    <UserPlus className="w-4 h-4" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">Step 2</Badge>
                </div>
                <h4 className="text-xs font-semibold text-foreground">Add Client / Debtor</h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                  Create customer accounts with GSTIN, billing address, and credit terms.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => navigate("/dashboard/customers")} className="w-full text-xs h-7 gap-1 mt-2">
                <span>Add Customer</span>
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-card/80 space-y-2 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="w-7 h-7 rounded-md bg-purple-500/10 text-purple-600 flex items-center justify-center">
                    <Receipt className="w-4 h-4" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">Step 3</Badge>
                </div>
                <h4 className="text-xs font-semibold text-foreground">Generate Tax Invoice</h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                  Issue your first professional GST-compliant invoice with QR code.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => navigate("/dashboard/invoices")} className="w-full text-xs h-7 gap-1 mt-2">
                <span>Create Invoice</span>
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>

            <div className="p-3.5 rounded-xl border border-border bg-card/80 space-y-2 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="w-7 h-7 rounded-md bg-amber-500/10 text-amber-600 flex items-center justify-center">
                    <Package className="w-4 h-4" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">Step 4</Badge>
                </div>
                <h4 className="text-xs font-semibold text-foreground">Stock & Purchases</h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                  Track inventory items, stock units, or record vendor bills.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => navigate("/dashboard/inventory")} className="w-full text-xs h-7 gap-1 mt-2">
                <span>Inventory</span>
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Widget Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 sm:gap-4 items-stretch">
        <AnimatePresence>
          {activeLayout
            .filter((item) => item.visible !== false && item.widgetId !== "live_clock")
            .map((item, index) => {
              const reg = WIDGET_REGISTRY[item.widgetId];
              const widgetTitle = reg?.title || item.widgetId;

              return (
                <WidgetWrapper
                  key={item.id}
                  id={item.id}
                  title={widgetTitle}
                  width={item.width}
                  isEditMode={isEditMode}
                  onRemove={() => handleRemoveWidget(item.widgetId)}
                  onToggleWidth={() => handleToggleWidth(index)}
                  onMoveUp={() => handleMoveUp(index)}
                  onMoveDown={() => handleMoveDown(index)}
                  isFirst={index === 0}
                  isLast={index === activeLayout.length - 1}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                >
                  {renderWidgetContent(item.widgetId, item.width)}
                </WidgetWrapper>
              );
            })}
        </AnimatePresence>
      </div>

      {/* Widget Library Modal */}
      <WidgetLibraryModal
        open={widgetLibraryOpen}
        onOpenChange={setWidgetLibraryOpen}
        activeLayout={activeLayout}
        onAddWidget={handleAddWidget}
        onRemoveWidget={handleRemoveWidget}
      />

      {/* In-Dashboard Quick Accounting Action Modals */}
      {/* 1. New Sale */}
      <GSTInvoiceForm
        type="Sales"
        isOpen={activeQuickAction === "new_sale"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        onSuccess={() => handleQuickActionSuccess("Sales invoice recorded successfully")}
      />

      {/* 2. Record Purchase (with Automatic Catalogue Item Creation) */}
      <GSTInvoiceForm
        type="Purchase"
        isOpen={activeQuickAction === "record_purchase"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        onSuccess={() => handleQuickActionSuccess("GST purchase bill recorded and inventory updated")}
      />

      {/* 3. Create Invoice */}
      <GSTInvoiceForm
        type="Sales"
        isOpen={activeQuickAction === "create_invoice"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        onSuccess={() => handleQuickActionSuccess("Tax invoice created successfully")}
      />

      {/* 4. Receive Payment */}
      <ReceiptVoucherForm
        isOpen={activeQuickAction === "receive_payment"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        onSuccess={() => handleQuickActionSuccess("Payment receipt recorded successfully")}
      />

      {/* 5. Record Expense */}
      <RecordExpenseDialog
        isOpen={activeQuickAction === "record_expense"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        onSuccess={() => handleQuickActionSuccess("Expense recorded successfully")}
      />

      {/* 6. Add Customer */}
      <QuickPartyModal
        open={activeQuickAction === "add_customer"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        partyType="customer"
        onPartyCreated={(party) => handleQuickActionSuccess(`Customer "${party.name}" created successfully`)}
      />

      {/* 7. Add Supplier */}
      <QuickPartyModal
        open={activeQuickAction === "add_supplier"}
        onOpenChange={(op) => { if (!op) setActiveQuickAction(null); }}
        partyType="supplier"
        onPartyCreated={(party) => handleQuickActionSuccess(`Supplier "${party.name}" created successfully`)}
      />
    </div>
  );
};
